/**
 * PAX Parameter Parser
 *
 * Handles parsing and serialization of room-based passenger data in URL parameters.
 *
 * URL Syntax: r{age1}-{age2}r{age3}-{age4}
 * - 'r' prefix before each room
 * - Ages separated by '-' within a room
 * - Adult age convention: 35 represents an adult
 *
 * @example
 * ```ts
 * // Parse URL parameter
 * const rooms = parsePaxString('r35-35r10-7');
 * // {
 * //   rooms: [
 * //     { persons: [{ type: 'adult' }, { type: 'adult' }] },
 * //     { persons: [{ type: 'child', age: 10 }, { type: 'child', age: 7 }] }
 * //   ]
 * // }
 *
 * // Serialize to URL
 * const pax = serializePaxString(rooms);
 * // "r35-35r10-7"
 * ```
 */

import { FilterValidation } from '@/utils/validation/filterValidation';
import type { RoomOccupancy, Person } from '@/types/SearchBar';

/**
 * Regex pattern for validating pax parameter format
 *
 * Requirements:
 * - Must start with 'r'
 * - Ages are numbers separated by '-'
 * - Each age may carry an optional trailing class letter: a=adult, c=child, i=infant
 *   (annotated form, emitted only on the offer→checkout hop). Plain forms (no letter)
 *   stay valid for search / SRP / sessionStorage / price-key.
 * - Each room starts with 'r'
 * - No empty rooms (at least one age per room)
 *
 * Valid (plain):     r35-35r10-7, r35, r35-10r35-7-5
 * Valid (annotated): r35a-1i-4c-16a, r35a
 * Invalid: 35-35, rr35, r35-, r-35
 */
export const PAX_ROOM_PATTERN = /^r\d+[aci]?(-\d+[aci]?)*(r\d+[aci]?(-\d+[aci]?)*)*$/;

/**
 * Parse pax string with room syntax into RoomOccupancy structure
 *
 * Splits by 'r' prefix to identify rooms, then parses ages within each room.
 * Validates all ages and returns null if any validation fails.
 *
 * @param paxString - URL parameter value (e.g., "r35-35r10-7")
 * @returns Parsed room occupancy structure or null if invalid
 *
 * @example
 * ```ts
 * parsePaxString('r35-35r10-7');
 * // {
 * //   rooms: [
 * //     { persons: [{ type: 'adult' }, { type: 'adult' }] },
 * //     { persons: [{ type: 'child', age: 10 }, { type: 'child', age: 7 }] }
 * //   ]
 * // }
 *
 * parsePaxString('35-35'); // null (missing 'r' prefix)
 * parsePaxString('rr35');  // null (empty room)
 * ```
 */
export function parsePaxString(paxString: string): RoomOccupancy | null {
  if (!paxString || paxString.trim().length === 0) {
    console.warn('[paxParser] Empty pax string');
    return null;
  }

  if (!validatePaxFormat(paxString)) {
    console.warn('[paxParser] Invalid pax format:', paxString);
    return null;
  }

  try {
    const roomStrings = paxString.split('r').filter((s) => s.length > 0);

    if (roomStrings.length === 0) {
      console.warn('[paxParser] No rooms found in pax string');
      return null;
    }

    const rooms = roomStrings.map((roomString, roomIndex) => {
      const ageStrings = roomString.split('-');
      const persons: Person[] = [];

      for (const token of ageStrings) {
        // Split each token into digits + optional class letter (a=adult, c=child, i=infant).
        const tokenMatch = token.match(/^(\d+)([aci])?$/);
        if (!tokenMatch) {
          throw new Error(`Invalid pax token in room ${roomIndex + 1}: ${token}`);
        }

        const age = FilterValidation.validatePassengerAge(tokenMatch[1]);
        if (age === null) {
          throw new Error(`Invalid age in room ${roomIndex + 1}: ${tokenMatch[1]}`);
        }

        const hotelClass =
          tokenMatch[2] === 'a'
            ? 'adult'
            : tokenMatch[2] === 'c'
              ? 'child'
              : tokenMatch[2] === 'i'
                ? 'infant'
                : undefined;

        // Binary `type` is derived from age as today; an under-18 hotel-adult (e.g. 16a)
        // is `type: 'child'` (age < 18) but `hotelClass: 'adult'`.
        const person: Person = age === 35 || age >= 18 ? { type: 'adult' } : { type: 'child', age };

        if (hotelClass) {
          person.hotelClass = hotelClass;
        }

        persons.push(person);
      }

      if (persons.length === 0) {
        throw new Error(`Empty room ${roomIndex + 1}`);
      }

      return { persons };
    });

    return { rooms };
  } catch (error) {
    console.error('[paxParser] Error parsing pax string:', paxString, error);
    return null;
  }
}

/**
 * Serialize RoomOccupancy structure to pax string
 *
 * Converts room-based structure to URL parameter format with 'r' prefixes.
 * Adults are serialized as age 35, children with their actual age.
 *
 * @param roomOccupancy - Room occupancy structure
 * @returns Pax string for URL (e.g., "r35-35r10-7") or empty string if invalid
 *
 * @example
 * ```ts
 * serializePaxString({
 *   rooms: [
 *     { persons: [{ type: 'adult' }, { type: 'adult' }] },
 *     { persons: [{ type: 'child', age: 10 }, { type: 'child', age: 7 }] }
 *   ]
 * });
 * // "r35-35r10-7"
 *
 * serializePaxString({ rooms: [] }); // ""
 * ```
 */
export function serializePaxString(roomOccupancy: RoomOccupancy): string {
  if (!roomOccupancy || !roomOccupancy.rooms || roomOccupancy.rooms.length === 0) {
    console.warn('[paxParser] Empty room occupancy, cannot serialize');
    return '';
  }

  try {
    const roomParts = roomOccupancy.rooms.map((room) => {
      if (!room.persons || room.persons.length === 0) {
        throw new Error('Room has no persons');
      }

      const ages = room.persons.map((person) => {
        if (person.type === 'adult') {
          return 35; // Adult convention
        } else if (person.type === 'child' && person.age !== undefined) {
          return person.age;
        } else {
          throw new Error(`Invalid person: ${JSON.stringify(person)}`);
        }
      });

      return `r${ages.join('-')}`;
    });

    return roomParts.join('');
  } catch (error) {
    console.error('[paxParser] Error serializing room occupancy:', roomOccupancy, error);
    return '';
  }
}

/**
 * Serialize RoomOccupancy to the ANNOTATED pax string (with class letters), used only
 * on the offer→checkout hop so the hotel's Adult/Child/Infant classification survives
 * the redirect (checkout never calls Player).
 *
 * Per person (reads `hotelClass` + `age`):
 * - `hotelClass: 'adult'` & ≥18 (age undefined or ≥18) → `35a`
 * - `hotelClass: 'adult'` & age < 18 (under-18 adult)   → `{age}a` (e.g. `16a`)
 * - `hotelClass: 'child'`                               → `{age}c`
 * - `hotelClass: 'infant'`                              → `{age}i`
 *
 * Returns `null` (never a partially-annotated string) if ANY person lacks `hotelClass`,
 * so the caller can fall back to the plain {@link serializePaxString}.
 *
 * @example
 * serializeAnnotatedPaxString({ rooms: [{ persons: [
 *   { type: 'adult', hotelClass: 'adult' },
 *   { type: 'child', age: 1, hotelClass: 'infant' },
 *   { type: 'child', age: 4, hotelClass: 'child' },
 *   { type: 'child', age: 16, hotelClass: 'adult' },
 * ] }] });
 * // "r35a-1i-4c-16a"
 */
export function serializeAnnotatedPaxString(roomOccupancy: RoomOccupancy): string | null {
  if (!roomOccupancy || !roomOccupancy.rooms || roomOccupancy.rooms.length === 0) {
    console.warn('[paxParser] Empty room occupancy, cannot serialize annotated pax');
    return null;
  }

  // Guard: never emit a partially-annotated string — all-or-nothing.
  const allClassified = roomOccupancy.rooms.every(
    (room) =>
      room.persons &&
      room.persons.length > 0 &&
      room.persons.every((person) => person.hotelClass !== undefined)
  );
  if (!allClassified) {
    console.warn(
      '[paxParser] A passenger lacks hotelClass; cannot build annotated pax (falling back to plain).'
    );
    return null;
  }

  try {
    const roomParts = roomOccupancy.rooms.map((room) => {
      const tokens = room.persons.map((person) => {
        const hotelClass = person.hotelClass!;

        if (hotelClass === 'adult') {
          // ≥18 adult (unknown age or ≥18) → 35 sentinel; under-18 adult → real age.
          return person.age === undefined || person.age >= 18 ? '35a' : `${person.age}a`;
        }

        // Children + infants always carry a real age.
        if (person.age === undefined) {
          throw new Error(`hotelClass '${hotelClass}' without age: ${JSON.stringify(person)}`);
        }
        return `${person.age}${hotelClass === 'child' ? 'c' : 'i'}`;
      });

      return `r${tokens.join('-')}`;
    });

    return roomParts.join('');
  } catch (error) {
    console.warn('[paxParser] Error serializing annotated pax; falling back to plain.', error);
    return null;
  }
}

/**
 * Validate pax string format using regex
 *
 * Checks if the pax string matches the expected syntax:
 * - Starts with 'r'
 * - Contains only numbers, '-', and 'r'
 * - No empty rooms (no consecutive 'r' or trailing '-')
 *
 * @param paxString - Pax string to validate
 * @returns True if format is valid, false otherwise
 *
 * @example
 * ```ts
 * validatePaxFormat('r35-35r10-7'); // true
 * validatePaxFormat('r35');         // true
 * validatePaxFormat('35-35');       // false (missing 'r')
 * validatePaxFormat('rr35');        // false (empty room)
 * validatePaxFormat('r35-');        // false (trailing dash)
 * ```
 */
export function validatePaxFormat(paxString: string): boolean {
  if (!paxString || typeof paxString !== 'string') {
    return false;
  }

  return PAX_ROOM_PATTERN.test(paxString.trim());
}
